import { create } from 'zustand';

import { FieldState } from 'support/types/ui';

import { findFirstFieldPathInDomOrder, ScrollToFieldOptions, scrollToNavigableItem } from './navigationHelpers';
import { NavigableItem } from './types';

const EMPTY_ITEMS: ReadonlyArray<never> = [];

/**
 * `NavigableItem` lives in `./types` so that `./navigationHelpers` can type against it without
 * importing this module, which imports `scrollToNavigableItem` from it. Re-exported here because
 * this module is the type's established import site across the app.
 */
export type { NavigableItem };

export abstract class NavigationService<T extends NavigableItem> {
  protected store: ReturnType<typeof createNavigationStore<T>>;
  protected virtualizedScrollCallbacks = new Map<string, (index: number) => void>();
  containerRef: React.RefObject<HTMLElement | null> | null = null;

  abstract focusOnCurrentItem(): void;

  constructor() {
    this.store = createNavigationStore<T>();
  }

  registerVirtualizedScroll = (tableId: string, scrollToIndex: (index: number) => void): void => {
    this.virtualizedScrollCallbacks.set(tableId, scrollToIndex);
  };

  unregisterVirtualizedScroll = (tableId: string): void => {
    this.virtualizedScrollCallbacks.delete(tableId);
  };

  scrollToVirtualizedRow = (tableId: string, rowIndex: number): void => {
    const scrollToIndex = this.virtualizedScrollCallbacks.get(tableId);
    if (scrollToIndex) {
      scrollToIndex(rowIndex);
    }
  };

  getCurrentItem = (): T | undefined => {
    return this.store.getState().items[this.store.getState().currentItemIndex];
  };

  getItems = (): Array<T> => {
    return this.store.getState().items;
  };

  getNextItem = (): T | undefined => {
    const state = this.store.getState();
    if (state.nextItemIndex < 0) {
      return undefined;
    }
    return state.items[state.nextItemIndex];
  };

  getPreviousItem = (): T | undefined => {
    const state = this.store.getState();
    if (state.previousItemIndex < 0) {
      return undefined;
    }
    return state.items[state.previousItemIndex];
  };

  getFirstNonPendingItem = (): T | undefined => {
    const state = this.store.getState();
    const { items, currentItemIndex, pendingFieldPaths } = state;

    if (currentItemIndex >= 0 && !isItemPending(items[currentItemIndex], pendingFieldPaths)) {
      return items[currentItemIndex];
    }

    const index = findNextNonPendingIndex(currentItemIndex, items, pendingFieldPaths);
    if (index >= 0) {
      return items[index];
    }

    return items[0];
  };

  resetFocus = (): void => {
    const firstNonPendingItem = this.getFirstNonPendingItem();
    if (firstNonPendingItem) {
      const index = this.store.getState().items.findIndex((e: T) => e.id === firstNonPendingItem.id);
      if (index !== -1) {
        this.store.getState().setCurrentItem(index);
      }
    }
    this.focusOnCurrentItem();
  };

  moveToPreviousItem = (): void => {
    if (this.store.getState().previousItemIndex >= 0) {
      this.store.getState().setCurrentItem(this.store.getState().previousItemIndex);
    }
    this.focusOnCurrentItem();
  };

  moveToNextItem = (): void => {
    if (this.store.getState().nextItemIndex >= 0) {
      this.store.getState().setCurrentItem(this.store.getState().nextItemIndex);
    }
    this.focusOnCurrentItem();
  };

  clearItems = (): void => {
    this.store.getState().clearItems();
  };

  useCurrentItem = () => {
    return this.store((state) => (state.currentItemIndex >= 0 ? state.items[state.currentItemIndex] : undefined));
  };

  useItems = () => {
    return this.store((state) => state.items);
  };

  useNumberOfActiveItems = (): number => {
    return this.useActiveItems().length;
  };

  getFirstItemByFieldPath = (fieldPath: string | undefined): T | undefined => {
    if (!fieldPath) {
      return undefined;
    }
    return this.store.getState().itemsByFieldPath[fieldPath]?.[0];
  };

  getItemsByFieldPath = (fieldPath: string): ReadonlyArray<T> => {
    return (this.store.getState().itemsByFieldPath[fieldPath] ?? EMPTY_ITEMS) as ReadonlyArray<T>;
  };

  useItemsByFieldPath = (fieldPath: string): ReadonlyArray<T> => {
    return this.store((state) => (state.itemsByFieldPath[fieldPath] ?? EMPTY_ITEMS) as ReadonlyArray<T>);
  };

  useActiveItems = (): Array<T> => {
    return this.store((state) => state.items.filter((item) => !isItemPending(item, state.pendingFieldPaths)));
  };

  // Items that are "live" in the nav cycle — non-pending. Subclasses MUST NOT
  // narrow this further (e.g. by filtering acknowledged annotations); that would
  // collapse this back into `useActiveItems` and defeat the purpose of having a
  // separate hook for cycle-visibility thresholds. The contract is: counts/gates
  // → useActiveItems (subclass may narrow); cycle visibility → useCycleableItems
  // (base only).
  useCycleableItems = (): Array<T> => {
    return this.store((state) => state.items.filter((item) => !isItemPending(item, state.pendingFieldPaths)));
  };

  useNumberOfCycleableItems = (): number => {
    return this.useCycleableItems().length;
  };

  useItemByFieldPath = (fieldPath: string): T | undefined => {
    return this.store((state) => {
      const currentItem = state.items[state.currentItemIndex];
      if (currentItem && currentItem.fieldPath === fieldPath) {
        return currentItem;
      }
      return state.itemsByFieldPath[fieldPath]?.[0];
    });
  };

  useFieldStateFromAnnotations = (fieldPath: string): FieldState => {
    const items = this.useItemsByFieldPath(fieldPath);
    return this.store((state) => {
      if (items.length === 0) {
        return 'default';
      }
      const item = items[0];
      if (isFormValidationError(item) && state.pendingFieldPaths.has(fieldPath)) {
        return 'pending-validation';
      }
      if (item.severity === 'error') {
        return 'error';
      }
      if (item.severity === 'warning') {
        return 'warning';
      }
      // ai_action items and items without an explicit severity (e.g. suggestion-style
      // items from collectChangedFields) render as 'default'. Producers that need the
      // error visual must set severity: 'error' explicitly.
      return 'default';
    });
  };

  /**
   * The field paths under `prefix` whose cells show an error or warning icon, as one sorted string. A string keeps
   * subscribers from re-rendering until the set of paths changes.
   */
  useIconFieldPathsKey = (prefix: string): string => {
    return this.store((state) =>
      Object.entries(state.itemsByFieldPath)
        .filter(
          ([fieldPath, items]) =>
            fieldPath.startsWith(prefix) && (items[0]?.severity === 'error' || items[0]?.severity === 'warning'),
        )
        .map(([fieldPath]) => fieldPath)
        .sort()
        .join('\n'),
    );
  };

  /**
   * Falls back to the first path given when none of them is on screen, so an unrendered field still
   * goes through the same not-found handling as any other missing anchor.
   */
  scrollToFirstField = (fieldPaths: Array<string>, options?: ScrollToFieldOptions): Promise<void> => {
    const fieldPath = findFirstFieldPathInDomOrder(fieldPaths, this.containerRef) ?? fieldPaths[0];
    return fieldPath ? this.scrollToField(fieldPath, options) : Promise.resolve();
  };

  scrollToField = (fieldPath: string, options?: ScrollToFieldOptions): Promise<void> => {
    return scrollToNavigableItem(
      { id: fieldPath, fieldPath, type: 'modified' },
      this.scrollToVirtualizedRow,
      this.containerRef,
      options,
    );
  };

  // Annotations carry UUID ids while form errors carry fieldPath ids; in both cases
  // the caller passes back an item from the current items array, so findIndex by id
  // resolves correctly.
  setCurrentItem = (item: Pick<T, 'id'>): number => {
    const state = this.store.getState();
    const index = state.items.findIndex((e: T) => e.id === item.id);
    if (index !== -1) {
      state.setCurrentItem(index);
      this.focusOnCurrentItem();
    }
    return index;
  };

  setItems = (items: Array<T>): void => {
    this.store.getState().setItems(items);
  };

  setFieldsPendingValidation = (fieldPaths: Array<string>): void => {
    this.store.getState().setFieldsPendingValidation(fieldPaths);
  };
}

type NavigationState<T extends NavigableItem> = {
  items: Array<T>;
  itemsByFieldPath: Record<string, Array<T>>;
  currentItemIndex: number;
  nextItemIndex: number;
  previousItemIndex: number;
  numberOfItems: number;
  pendingFieldPaths: Set<string>;

  setCurrentItem: (itemIndex: number) => void;
  clearItems: () => void;
  setItems: (items: Array<T>) => void;
  setFieldsPendingValidation: (fieldPaths: Array<string>) => void;
};

export const isItemPending = (item: NavigableItem | undefined, pendingFieldPaths: Set<string>): boolean => {
  if (!item) {
    return false;
  }
  return isFormValidationError(item) && pendingFieldPaths.has(item.fieldPath);
};

const isFormValidationError = (item: NavigableItem): boolean => {
  return item.isFormError === true;
};

const findNextNonPendingIndex = <T extends NavigableItem>(
  currentIndex: number,
  items: Array<T>,
  pendingFieldPaths: Set<string>,
): number => {
  if (items.length === 0) {
    return -1;
  }

  let nextIndex = (currentIndex + 1) % items.length;
  let checked = 0;

  while (checked < items.length) {
    if (!isItemPending(items[nextIndex], pendingFieldPaths)) {
      return nextIndex;
    }
    nextIndex = (nextIndex + 1) % items.length;
    checked++;
  }

  return -1;
};

const NO_CURSOR_YET = -1;
const findPreviousNonPendingIndex = <T extends NavigableItem>(
  currentIndex: number,
  items: Array<T>,
  pendingFieldPaths: Set<string>,
): number => {
  if (items.length === 0) {
    return -1;
  }

  const length = items.length;
  const start = currentIndex === NO_CURSOR_YET ? length : currentIndex;
  let prevIndex = (start - 1 + length) % length;
  let checked = 0;

  while (checked < items.length) {
    if (!isItemPending(items[prevIndex], pendingFieldPaths)) {
      return prevIndex;
    }
    prevIndex = (prevIndex - 1 + length) % length;
    checked++;
  }

  return -1;
};

const resolveCursorAfterRebuild = <T extends NavigableItem>(
  prevState: NavigationState<T>,
  newItems: Array<T>,
): number => {
  if (prevState.currentItemIndex === NO_CURSOR_YET) {
    return NO_CURSOR_YET;
  }
  const prevItem = prevState.items[prevState.currentItemIndex];
  if (!prevItem) {
    return 0;
  }

  const newIndex = newItems.findIndex((item) => item.id === prevItem.id);
  if (newIndex !== -1) {
    return newIndex;
  }

  for (let offset = 1; offset < prevState.items.length; offset++) {
    const oldIndex = (prevState.currentItemIndex + offset) % prevState.items.length;
    const oldItem = prevState.items[oldIndex];
    const newIndex = newItems.findIndex((item) => item.id === oldItem.id);
    if (newIndex !== -1) {
      return newIndex;
    }
  }

  return 0;
};

const buildItemsByFieldPath = <T extends NavigableItem>(
  items: Array<T>,
  prev: Record<string, Array<T>>,
): Record<string, Array<T>> => {
  const grouped: Record<string, Array<T>> = {};
  for (const item of items) {
    const fp = item.fieldPath;
    if (!grouped[fp]) {
      grouped[fp] = [];
    }
    grouped[fp].push(item);
  }

  const result: Record<string, Array<T>> = {};
  for (const fp of Object.keys(grouped)) {
    const newList = grouped[fp];
    const prevList = prev[fp];
    if (prevList && prevList.length === newList.length && prevList.every((item, idx) => item === newList[idx])) {
      result[fp] = prevList;
    } else {
      result[fp] = newList;
    }
  }
  return result;
};

const createNavigationStore = <T extends NavigableItem>() => {
  return create<NavigationState<T>>()((set) => ({
    items: [],
    itemsByFieldPath: {},
    currentItemIndex: NO_CURSOR_YET,
    nextItemIndex: NO_CURSOR_YET,
    previousItemIndex: NO_CURSOR_YET,
    numberOfItems: 0,
    pendingFieldPaths: new Set(),

    setItems: (items: Array<T>) => {
      set((state) => {
        const itemsByFieldPath = buildItemsByFieldPath(items, state.itemsByFieldPath);

        const numberOfItems = items.length;
        const { pendingFieldPaths } = state;
        let currentItemIndex = NO_CURSOR_YET;
        let nextItemIndex = NO_CURSOR_YET;
        let previousItemIndex = NO_CURSOR_YET;

        if (items.length > 0) {
          currentItemIndex = resolveCursorAfterRebuild(state, items);
          nextItemIndex = findNextNonPendingIndex(currentItemIndex, items, pendingFieldPaths);
          previousItemIndex = findPreviousNonPendingIndex(currentItemIndex, items, pendingFieldPaths);
        }

        return {
          ...state,
          items,
          itemsByFieldPath,
          numberOfItems,
          currentItemIndex,
          nextItemIndex,
          previousItemIndex,
        };
      });
    },

    setCurrentItem: (itemIndex: number) => {
      set((state) => {
        const { items, pendingFieldPaths } = state;
        const itemsLength = items.length;

        if (itemsLength === 0) {
          return state;
        }

        let currentItemIndex = itemIndex;
        if (currentItemIndex < 0) {
          currentItemIndex = itemsLength - 1;
        } else if (currentItemIndex >= itemsLength) {
          currentItemIndex = 0;
        }

        const nextItemIndex = findNextNonPendingIndex(currentItemIndex, items, pendingFieldPaths);
        const previousItemIndex = findPreviousNonPendingIndex(currentItemIndex, items, pendingFieldPaths);

        return {
          ...state,
          currentItemIndex,
          nextItemIndex,
          previousItemIndex,
        };
      });
    },

    clearItems: () => {
      set({
        items: [],
        itemsByFieldPath: {},
        currentItemIndex: NO_CURSOR_YET,
        nextItemIndex: NO_CURSOR_YET,
        previousItemIndex: NO_CURSOR_YET,
        numberOfItems: 0,
        pendingFieldPaths: new Set(),
      });
    },

    setFieldsPendingValidation: (fieldPaths: Array<string>) => {
      set((state) => {
        const pendingFieldPaths = new Set(fieldPaths);
        const { items, currentItemIndex } = state;

        const nextItemIndex = findNextNonPendingIndex(currentItemIndex, items, pendingFieldPaths);
        const previousItemIndex = findPreviousNonPendingIndex(currentItemIndex, items, pendingFieldPaths);

        return {
          ...state,
          pendingFieldPaths,
          nextItemIndex,
          previousItemIndex,
        };
      });
    },
  }));
};
